import { useQuery } from '@tanstack/react-query';
import type { FeatureKey } from '@oip/contracts';
import { endpoints } from '../api/endpoints';
import { useTenantKey } from '../hooks/api';

/**
 * Features enabled for the active tenant.
 *
 * Read from its own endpoint rather than from `useAuth`, for the same reason
 * units are: an entitlement changes without the session changing, so a grant
 * made by a platform administrator must reach an open tab without a re-login.
 *
 * Drives NAVIGATION and route gating only. Every guarded endpoint refuses
 * independently, because hiding a link has never stopped anyone typing a URL.
 *
 * Defaults to NOTHING while loading. The alternative — assuming a feature is on
 * until told otherwise — would flash a page the tenant has not bought and then
 * snatch it away, which reads as a fault rather than as a plan boundary.
 */
export function useFeatures(): { features: FeatureKey[]; loading: boolean } {
  const tenantKey = useTenantKey();
  const { data, isLoading } = useQuery({
    queryKey: ['tenant-features', tenantKey],
    queryFn: () => endpoints.features(),
    // Configuration, not data. The platform screen invalidates this key when it
    // grants or revokes, so a change is still immediate.
    staleTime: 60 * 60_000,
    retry: false,
  });
  const features = (data?.features ?? []).filter((f) => f.enabled).map((f) => f.feature);
  return { features, loading: isLoading };
}
